import React, {useState} from 'react';
import styled, {ThemeProvider} from 'styled-components';
import {CopyToClipboard} from 'react-copy-to-clipboard';
import {FormattedMessage} from '@kepler.gl/localization';
import {ActionHandler, setExportHTMLMapMode, setMapInfo} from '@kepler.gl/actions';
import {MapInfo} from '@kepler.gl/types';
import {injectIntl} from 'react-intl';
import {IntlShape} from 'react-intl';
import {MapObject} from './share-map-manager';
import get from 'lodash.get';

import {
  StyledModalContent,
  StyledExportSection,
  StyledType,
  InputLight,
  TextAreaLight,
  Button,
  CheckMark,
  ModalTabsFactory
} from '@kepler.gl/components';

import {
  EXPORT_HTML_MAP_MODE_OPTIONS,
  EXPORT_HTML_MAP_MODES_DOC,
  MAP_INFO_CHARACTER
} from '@kepler.gl/constants';
import axios from 'axios';

const host = process.env.API_URL;

const ExportMapStyledExportSection = styled(StyledExportSection)`
  .disclaimer {
    font-size: ${props => props.theme.inputFontSize};
    color: ${props => props.theme.inputColor};
    margin-top: 12px;
  }
`;

interface StyledInputProps {
  error?: boolean;
}

const StyledInput = styled.input<StyledInputProps>`
  width: 100%;
  padding: ${props => props.theme.inputPadding};
  color: ${props => (props.error ? 'red' : props.theme.titleColorLT)};
  height: ${props => props.theme.inputBoxHeight};
  outline: 0;
  font-size: ${props => props.theme.inputFontSize};

  :active,
  :focus,
  &.focus,
  &.active {
    outline: 0;
  }
`;

const BigStyledTile = styled(StyledType)`
  height: unset;
  width: unset;
  img {
    width: 180px;
    height: 120px;
  }
`;

export const StyledInputLabel = styled.label`
  font-size: 12px;
  color: ${props => props.theme.textColorLT};
  letter-spacing: 0.2px;
`;

export const StyleSharingUrl = styled.div.attrs({
  className: 'sharing-url'
})`
  width: 100%;
  display: flex;
  margin-bottom: 14px;
  flex-direction: column;

  input {
    border-right: 0;
  }

  .button {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

interface SharingUrlProps {
  url: string;
  message?: string;
}

export const SharingUrl: React.FC<SharingUrlProps> = ({url, message = ''}) => {
  const [copied, setCopy] = useState(false);
  return (
    <StyleSharingUrl>
      <StyledInputLabel>{message}</StyledInputLabel>
      <div style={{display: 'flex'}}>
        <InputLight type="text" value={url} readOnly />
        <CopyToClipboard text={url} onCopy={() => setCopy(true)}>
          <Button width="80px">{copied ? 'Copied!' : 'Copy'}</Button>
        </CopyToClipboard>
      </div>
    </StyleSharingUrl>
  );
};
const nop = () => {};

const StyledShareMapModal = styled(StyledModalContent)`
  padding: 24px 72px 40px 72px;
  margin: 0 -72px -40px -72px;
`;

const StyledInnerDiv = styled.div`
  min-height: 500px;
`;

type CharacterLimits = {
  title?: number;
  description?: number;
};

interface ShareMapUrlModalFactoryProps {
  map: MapObject;
  mapInfo: MapInfo;
  characterLimits?: CharacterLimits;
  onChangeExportMapHTMLMode?: ActionHandler<typeof setExportHTMLMapMode>;
  generateUrl?: () => Promise<string>;
  onSetMapInfo: typeof setMapInfo;
  options: {
    mode?: string;
  };
  mapUrl: string;
  newMap: boolean;
  onBack: () => void;
  onSave: () => void;
}

type IntlProps = {
  intl: IntlShape;
};

const getDefaultMethod = <T,>(methods: T[] = []) =>
  Array.isArray(methods) ? get(methods, [0]) : null;
export interface LoadingMethod {
  id: string;
  label: string;
  elementType: React.ComponentType<any>;
  tabElementType?: React.ComponentType<{onClick: React.MouseEventHandler; intl: IntlShape}>;
}

export default function ShareMapUrlModalFactory() {
  const ShareMapUrlModal: React.FC<ShareMapUrlModalFactoryProps & IntlProps> = ({
    characterLimits,
    map = {} as MapObject,
    generateUrl = () => '',
    onChangeExportMapHTMLMode = mode => {},
    onSetMapInfo,
    options = {},
    mapInfo = {title: '', description: '', password: ''},
    intl,
    mapUrl,
    newMap,
    onBack = () => null,
    onSave = () => null,
  }) => {
    const [url, setUrl] = useState(mapUrl);
    const [saveError, setSaveError] = useState('');
    const [generateClicked, setGenerateClicked] = useState(false);
    const [mapData, setMapData] = useState(map);
    const [mapEnabled, setMapEnabled] = useState(map.enabled);

    const onChangeInput = (
      key: string,
      {target: {value}}: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
      setSaveError('');
      setMapData(previousMapData => ({
        ...previousMapData,
        [key]: value
      }));
    };
    
    const handleGenerateUrl = async () => {
      if (!generateClicked) {
        setGenerateClicked(true);
        if (!url) {
          const mapJSON = await generateUrl();
          const generatedUrl = await generateMap(mapJSON);
          setUrl(generatedUrl);
          setGenerateClicked(false);
        };
      }
    };

    const saveShareMap = async () => {
      try {
        const payload = {
          title: mapData.title,
          description: mapData.description,
          share_type: options.mode,
          enabled: mapEnabled,
          password: mapData.password
        };
        const token = sessionStorage.getItem('token');
        const response = await axios.put(`${host}/api/maps/${map.id}`, payload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }});
      } catch (error) {
        console.error('Error saving map:', error);
      }
    };

    const handleSave = async () => {
      if (mapData.title == '' || mapData.description == '' || !options.mode) {
        setSaveError('Title, description and map mode must be specified.')
      } else if (!url) {
        setSaveError('A URL must be generated.')
      } else if (newMap) {
        onSave();
      } else {
        await saveShareMap();
        onSave();
      }
    };

    const onChangeActive = () => {
      setMapEnabled(!mapEnabled);
    };

    const generateMap = async (mapJSON) => {
      // generate html
      console.log(options.mode);
      const mode = options.mode;
      const {title, description, password} = mapData;
      
      const data = {
        ...mapJSON,
        mapboxApiAccessToken: '{{ MAPBOX_TOKEN }}',
        title,
        description,
        mode
      };
      const token = sessionStorage.getItem('token');
      const filename_prefix = title.replace(/\s+/g, '-');
      const shareResponse = await axios.post(`${host}/api/files`, 
        {
          'filename': `shared_map_data_${filename_prefix}.json`,
          'file_category': 'data'
        }, 
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      )
        .then(response => {
          const { url } = response.data.body;
          
          return fetch(url, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(data.datasets)
          });
        })
        // This is put sequentially so that it works locally.
        // TODO: fix the local api so that it supports multiple requests at once
        .then(() => {
          return axios.post(`${host}/api/files`, 
            {
              'filename': `shared_map_config_${filename_prefix}.json`,
              'file_category': 'config'
            },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          )
        })
        .then(response => {
          const { url } = response.data.body;
          
          return fetch(url, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(data.config)
          });
        })
        .then(() => {
          return axios.post(`${host}/api/maps/share`, 
            {
              'title': title,
              'description': description,
              'share_type': mode,
              'data_id': `shared_map_data_${filename_prefix}.json`,
              'config_id': `shared_map_config_${filename_prefix}.json`,
              'password': password
            },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          )
        })
        .then(response => {
          const generatedUrl = response.data.body.url;
          return generatedUrl;
        })
        .catch(error => {
          console.error(error);
        });

      const generatedUrl = shareResponse;
      return generatedUrl;
    };

    return (
        <StyledInnerDiv>
            <StyledExportSection>
                <div className="description">
                <div className="title">
                    <FormattedMessage id="Title"/>
                </div>
                <div className="subtitle">
                    <FormattedMessage id={'Only alphanumeric and spaces allowed. Maximum of 32 characters.'} />
                </div>
                </div>
                <div className="selection">
                <StyledInput
                    type="text"
                    value={mapData.title}
                    onChange={e => onChangeInput('title', e)}
                    placeholder="Type map title"
                    pattern="[a-zA-Z0-9\s]+"
                    maxLength={32}
                />
                </div>
            </StyledExportSection>
            <StyledExportSection>
                <div className="description">
                <div className="title">
                    <FormattedMessage id="Description"/>
                </div>
                <div className="subtitle">
                    <FormattedMessage id={'Only alphanumeric and spaces allowed.'} />
                </div>
                </div>
                <div className="selection">
                <TextAreaLight
                    rows={5}
                    id="map-description"
                    style={{resize: 'none'}}
                    value={mapData.description}
                    onChange={e => onChangeInput('description', e)}
                    placeholder="Type map description"
                />
                </div>
            </StyledExportSection>
            <ExportMapStyledExportSection>
                <div className="description">
                <div className="title">
                    <FormattedMessage id={'modal.exportMap.html.modeTitle'} />
                </div>
                <div className="subtitle">
                    <FormattedMessage id="Select the share map mode." />
                </div>
                </div>
                <div className="selection">
                {EXPORT_HTML_MAP_MODE_OPTIONS.map(mode => (
                    <BigStyledTile
                    key={mode.id}
                    selected={options.mode === mode.id}
                    onClick={() => mode.available && onChangeExportMapHTMLMode(mode.id)}
                    >
                    <img src={mode.url} alt="" />
                    <p>
                        <FormattedMessage
                        id={'modal.exportMap.html.modeDescription'}
                        values={{mode: intl.formatMessage({id: mode.label})}}
                        />
                    </p>
                    {options.mode === mode.id && <CheckMark />}
                    </BigStyledTile>
                ))}
                </div>
            </ExportMapStyledExportSection>
            <StyledExportSection>
                <div className="description">
                <div className="title">
                    <FormattedMessage id="Map password"/>
                </div>
                <div className="subtitle">
                    <FormattedMessage id={'(Optional) Set password for accessing share map. Only alphanumeric characters are accepted, up to 32 characters.'} />
                </div>
                </div>
                <div className="selection">
                <StyledInput
                    type="text" // TODO: Set this as password and include a show password button
                    value={mapData.password}
                    onChange={e => onChangeInput('password', e)}
                    placeholder=""
                    pattern="[a-zA-Z0-9]+"
                    maxLength={32}
                />
                </div>
            </StyledExportSection>
            <StyledExportSection>
                <div className="description">
                <div className="title">
                    <FormattedMessage id="Active"/>
                </div>
                <div className="subtitle">
                    <FormattedMessage id={'Set whether the share map link is accessible.'} />
                </div>
                </div>
                <div className="selection">
                <StyledInput
                  type="checkbox"
                  checked={mapEnabled}
                  onChange={onChangeActive}
                />
                </div>
            </StyledExportSection>
            <StyledExportSection>
                <div className="description">
                <div className="title">
                    <FormattedMessage id={'modal.shareMap.shareUriTitle'} />
                </div>
                <div className="subtitle">
                    <FormattedMessage id={'modal.shareMap.shareUriSubtitle'} />
                </div>
                </div>
                <div className="selection">
                {url ? <SharingUrl url={url} message="Your sharing URL"/> : 
                <Button onClick={handleGenerateUrl} disabled={generateClicked}>
                    <FormattedMessage id={'Generate URL'} />
                </Button>}
                </div>
            </StyledExportSection>
            <StyledExportSection>
              {(saveError ) ? <div style={{ textAlign: 'right', color: 'red' }}>{saveError}</div> : null}
              <div style={{ position: 'absolute', bottom: '20px', right: '20px' }}>
                <Button onClick={onBack} style={{ backgroundColor: 'lightgrey'}}>Cancel</Button>
                <Button onClick={handleSave} style={{ marginLeft: '10px' }}>Save</Button>
              </div>
            </StyledExportSection>
        </StyledInnerDiv>
    );
  };

  ShareMapUrlModal.defaultProps = {
    characterLimits: MAP_INFO_CHARACTER,
  }

  return injectIntl(ShareMapUrlModal);
}
